import { clx } from "@/utils";
import { Copy, Play, Pause } from "lucide-react";
import { FiMaximize2, FiMinimize2 } from "react-icons/fi";
import { MdClose } from "react-icons/md";
import { toast } from "sonner";
import * as Portal from "@radix-ui/react-portal";
import { IosLoader } from "../Loading/Loading";
import IconButton from "../Micro/IconButton";

type ModalPortalContentProps = {
  minimized: boolean;
  title: string;
  timer: number;
  logName: string;
  time: string;
  isPaused: boolean;
  pendingLogsCount: number;
  togglePause: () => void;
  handleCloseLogModal: (e?: React.MouseEvent) => void;
  handleMinizeModal: (e?: React.MouseEvent) => void;
  handleMaximizeModal: (e?: React.MouseEvent) => void;
  type: string;
  failed: boolean;
  copyEnabled: boolean;
  pauseEnabled: boolean;
  minMaxEnabled: boolean;
};

const ModalHeaderContent = ({
  minimized,
  title,
  timer,
  logName,
  time,
  isPaused,
  pendingLogsCount,
  togglePause,
  handleCloseLogModal,
  handleMinizeModal,
  handleMaximizeModal,
  type,
  failed,
  copyEnabled = true,
  pauseEnabled = true,
  minMaxEnabled = true,
}: ModalPortalContentProps) => {
  const headerContent = (
    <div
      className={clx(
        "flex w-full flex-row items-center justify-between space-x-3",
        minimized && [
          "border-b border-gray-200 dark:border-gray-800",
          "fixed bottom-4 left-1/2 -translate-x-1/2",
          "z-50",
          "bg-light dark:bg-theme-color-light",
          "shadow-md",
          "p-4",
          "max-w-2xl",
        ],
      )}
    >
      <h1 className="text-md whitespace-nowrap font-semibold text-gray-700 dark:text-gray-500">
        {title ? (
          title
        ) : timer === 0 ? (
          "Deployment logs"
        ) : (
          <span className="truncate">
            Deployment {minimized ? "..." : "started"} {time} ago
          </span>
        )}
      </h1>
      <div className="flex flex-row items-center space-x-3">
        {copyEnabled && (
          <IconButton
            title="Copy Log Name"
            className="btn-ghost btn-sm bg-light text-primary hover:bg-theme-color-lighter"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(logName || title || "");
              toast.success("Log name copied to clipboard");
            }}
          >
            <Copy size={15} />
          </IconButton>
        )}

        {pauseEnabled && (
          <IconButton
            title={
              isPaused
                ? `Resume Logs${
                    pendingLogsCount ? ` (${pendingLogsCount} pending)` : ""
                  }`
                : "Pause Logs"
            }
            className="btn-ghost btn-sm bg-light text-primary hover:bg-theme-color-lighter"
            onClick={(e) => {
              e.stopPropagation();
              togglePause();
            }}
          >
            {isPaused ? <Play size={15} /> : <Pause size={15} />}
          </IconButton>
        )}

        {minMaxEnabled && (
          <IconButton
            title={minimized ? "Maximize" : "Minimize"}
            className="btn-ghost btn-sm bg-light text-primary transition-all duration-300 ease-in-out hover:bg-light"
            onClick={(e) => {
              e.stopPropagation();
              minimized ? handleMaximizeModal(e) : handleMinizeModal(e);
            }}
          >
            {minimized ? <FiMaximize2 size={15} /> : <FiMinimize2 size={15} />}
          </IconButton>
        )}

        {type === "history" || failed ? (
          <IconButton
            className="btn-ghost btn-sm bg-light text-primary hover:bg-light"
            onClick={(e) => {
              e.stopPropagation();
              handleCloseLogModal(e);
            }}
          >
            <MdClose size={15} />
          </IconButton>
        ) : (
          <>
            {timer === 0 || minimized ? (
              <IconButton
                className="btn-ghost btn-sm bg-light text-primary hover:bg-light"
                onClick={(e) => {
                  // e.stopPropagation();
                  handleCloseLogModal(e);
                }}
              >
                <MdClose size={15} />
              </IconButton>
            ) : (
              <div className="relative h-8 w-8 scale-50">
                <IosLoader />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (minimized) {
    return <Portal.Root>{headerContent}</Portal.Root>;
  }

  return headerContent;
};

export default ModalHeaderContent;
